/**
 * Mensagens pós-venda do Mercado Livre (SAC): caixa de entrada por carrinho, com as que esperam resposta primeiro e o
 * prazo do agente de IA do ML (48 h úteis). Conversa ao lado, com o pedido, anexos e o motivo quando está bloqueada.
 * Pedido sem conversa (Mercado Envios): iniciar só pelos motivos do ML (modelo pronto ou texto livre, com limite).
 * Abrir pelo pedido: /canal/mercadolivre/mensagens?order=<id do pedido>.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Check, CheckCheck, Lock, Paperclip, RefreshCw, Search, Send, X } from 'lucide-react';
import { api, brl, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { Head, Pager, useSearchBox, useUrlState } from './MeliChannel.tsx';
import { ago } from './Questions.tsx';

interface OrderSummary { id: string; externalId: string; buyer: string | null; status: string; shipmentStatus: string | null; logisticType: string | null; total: number | null; items: string | null; at: string | null }
interface Conv {
  id: string; packId: string; account: string | null; status: string | null; substatus: string | null; blockReason: string | null; unread: number; messages: number;
  lastAt: string | null; lastFrom: string | null; lastText: string | null; waitingSince: string | null; order: OrderSummary | null;
}
interface ListPage { accounts: { id: string; name: string }[]; counts: { waiting: number; late: number; unread: number; blocked: number }; total: number; page: number; pageSize: number; rows: Conv[] }
interface Msg {
  id: string; fromSeller: boolean; fromAgent: boolean; text: string; status: string | null; moderation: { status: string; reason: string | null } | null;
  attachments: { filename: string; name: string; type: string | null; size: number | null }[]; sentAt: string; readAt: string | null; sentBy: string | null;
}
interface Thread {
  id: string; packId: string; status: string | null; substatus: string | null; blockReason: string | null; claimId: string | null; viaAgent: boolean;
  maxLength: number; buyerMessages: number; waitingSince: string | null; syncError: string | null; order: OrderSummary | null; messages: Msg[];
}
interface Option { id: string; label: string; kind: 'template' | 'text'; templates: { id: string; text: string | null }[]; charLimit: number | null; capAvailable: number | null }

/** Prazo do agente de IA: 48 horas contadas só em dias úteis (seg a sex). */
export function businessDeadline(fromIso: string, hours = 48): Date {
  const d = new Date(fromIso);
  let left = hours * 60;
  while (left > 0) {
    d.setMinutes(d.getMinutes() + 30);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) left -= 30;
  }
  return d;
}
const until = (d: Date) => {
  const m = Math.round((d.getTime() - Date.now()) / 60_000);
  if (m <= 0) return 'prazo vencido';
  return m < 60 ? `faltam ${m} min` : m < 2880 ? `faltam ${Math.floor(m / 60)} h` : `faltam ${Math.floor(m / 1440)} dias`;
};
const SHIP: Record<string, string> = { pending: 'pendente', handling: 'preparando', ready_to_ship: 'pronto para enviar', shipped: 'a caminho', delivered: 'entregue', not_delivered: 'não entregue', cancelled: 'cancelado' };
const LOG: Record<string, string> = { fulfillment: 'Full', self_service: 'Flex', cross_docking: 'Coleta', drop_off: 'Agência', xd_drop_off: 'Places' };
const MOD: Record<string, string> = {
  OUT_OF_PLACE_LANGUAGE: 'linguagem inadequada', SOCIAL_NETWORK_LINK: 'link de rede social', LINK_SHORT_URL: 'link encurtado', AUTOMATIC_MESSAGE: 'mensagem automática',
  PERSONAL_DATA: 'dados pessoais', LINK_MERCADOPAGO: 'link do Mercado Pago', ML_LINKS_PAYPAL: 'link de pagamento', EVASION_CLAIM_SELLER: 'tentativa de evitar reclamação',
};

export function MessagesPage() {
  const qc = useQueryClient();
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const state = params.get('state') ?? 'waiting';
  const page = Number(params.get('page') ?? 1);
  const selected = params.get('c');
  const filter: Record<string, string> = { state };
  for (const k of ['account', 'q']) if (params.get(k)) filter[k] = params.get(k)!;
  const qs = new URLSearchParams({ ...filter, page: String(page) });
  const list = useQuery({
    queryKey: ['messages', qs.toString()],
    queryFn: () => api<ListPage>(`/channels/mercadolivre/messages?${qs}`),
    placeholderData: keepPreviousData, refetchInterval: 30_000,
  });
  // Aberta pelo pedido: cria/lê a conversa do carrinho e seleciona.
  const orderId = params.get('order');
  const open = useMutation({
    mutationFn: (id: string) => api<{ id: string }>('/channels/mercadolivre/messages/open', { method: 'POST', json: { orderId: id } }),
    onSuccess: (r) => set({ order: null, c: r.id, state: 'all' }),
  });
  useEffect(() => { if (orderId && !open.isPending) open.mutate(orderId); }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps
  const sync = useMutation({
    mutationFn: () => api('/channels/mercadolivre/messages/sync', { method: 'POST', json: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messages'] }),
  });
  const d = list.data;
  const c = d?.counts;

  return (
    <>
      <Head title="Mensagens">
        <p>Conversas pós-venda do Mercado Livre, uma por carrinho. As que esperam resposta ficam no topo. Com o assistente de IA do ML, há 48 horas úteis para resolver antes de a conversa ser bloqueada.</p>
      </Head>
      <div className="stack-y">
        {open.error && <ErrorBox error={open.error} />}
        {list.error ? <ErrorBox error={list.error} /> : !d || !c ? <Loading /> : (
          <>
            <div className="toolbar">
              <div className="seg" role="group" aria-label="Situação">
                {([['waiting', `Esperando resposta (${int(c.waiting)})`], ['unread', `Não lidas (${int(c.unread)})`], ['all', 'Todas'], ['blocked', `Bloqueadas (${int(c.blocked)})`]] as const).map(([v, l]) => (
                  <button key={v} type="button" aria-pressed={state === v} onClick={() => set({ state: v === 'waiting' ? null : v, page: null })}>{l}</button>
                ))}
              </div>
              {c.late > 0 && <Tag tone="err">{int(c.late)} esperando há mais de 24 h</Tag>}
              <label className="search">
                <Search size={16} />
                <span className="sr-only">Buscar</span>
                <input className="input" type="search" placeholder="Pedido, carrinho, comprador ou produto" value={search} onChange={(e) => setSearch(e.target.value)} />
              </label>
              <span className="grow" />
              <button type="button" className="btn small" disabled={sync.isPending} onClick={() => sync.mutate()}><RefreshCw size={14} /> {sync.isPending ? 'Lendo…' : 'Ler agora'}</button>
            </div>
            {sync.error && <ErrorBox error={sync.error} />}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 380px) 1fr', gap: 12, alignItems: 'start' }}>
              <div className="panel flush" style={{ opacity: list.isPlaceholderData ? 0.55 : 1 }}>
                {d.rows.length === 0 ? <div className="empty"><b>{state === 'waiting' ? 'Nenhuma conversa esperando' : 'Nada por aqui'}</b>As mensagens novas chegam sozinhas.</div> : d.rows.map((r) => {
                  const deadline = r.waitingSince ? businessDeadline(r.waitingSince) : null;
                  const late = deadline && deadline.getTime() - Date.now() < 12 * 3_600_000;
                  return (
                    <button key={r.id} type="button" onClick={() => set({ c: r.id })}
                      style={{ display: 'grid', gap: 2, width: '100%', textAlign: 'left', padding: '10px 12px', border: 0, borderBottom: '1px solid var(--line)', cursor: 'pointer', font: 'inherit', color: 'inherit',
                        background: selected === r.id ? 'var(--surface-2)' : 'transparent', borderLeft: r.waitingSince ? `3px solid ${late ? 'var(--err)' : 'var(--warn)'}` : '3px solid transparent' }}>
                      <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <b style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.order?.buyer ?? `Carrinho ${r.packId}`}</b>
                        {r.unread > 0 && <Tag tone="info">{r.unread}</Tag>}
                        {r.status === 'blocked' && <Lock size={13} />}
                        <span className="small muted">{ago(r.lastAt)}</span>
                      </span>
                      <span className="small muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.order?.items ?? `Pedido ${r.order?.externalId ?? r.packId}`}</span>
                      <span className="small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.lastFrom === 'seller' ? 'Você: ' : ''}{r.lastText}</span>
                      {deadline && <span className="small" style={{ color: late ? 'var(--err)' : 'var(--warn)' }}>esperando {ago(r.waitingSince).replace('há ', '')} · {until(deadline)}</span>}
                    </button>
                  );
                })}
                <Pager total={d.total} page={page} pageSize={d.pageSize} onPage={(p) => set({ page: p > 1 ? String(p) : null })} />
              </div>
              {selected ? <ThreadView key={selected} id={selected} onClose={() => set({ c: null })} />
                : <div className="panel"><div className="empty"><b>Escolha uma conversa</b>Ou abra pelo pedido (botão Mensagens no detalhe do pedido).</div></div>}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function ThreadView({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const [firstLoad, setFirstLoad] = useState(true);
  const t = useQuery({
    queryKey: ['message-thread', id],
    // A primeira leitura marca como lidas no ML (alguém abriu a conversa); as atualizações seguintes não.
    queryFn: async () => { const r = await api<Thread>(`/channels/mercadolivre/messages/${id}${firstLoad ? '?read=1' : ''}`); setFirstLoad(false); return r; },
    refetchInterval: 20_000,
  });
  useEffect(() => { void qc.invalidateQueries({ queryKey: ['messages'] }); }, [t.data?.messages.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [t.data?.messages.length]);
  const send = useMutation({
    mutationFn: async () => {
      const attachments = await Promise.all(files.map(async (f) => ({
        filename: f.name, contentType: f.type || 'application/octet-stream',
        base64: btoa(Array.from(new Uint8Array(await f.arrayBuffer()), (b) => String.fromCharCode(b)).join('')),
      })));
      return api(`/channels/mercadolivre/messages/${id}/send`, { method: 'POST', json: { text, attachments } });
    },
    onSuccess: () => { setText(''); setFiles([]); void qc.invalidateQueries({ queryKey: ['message-thread', id] }); void qc.invalidateQueries({ queryKey: ['messages'] }); },
  });
  const th = t.data;
  const bad = useMemo(() => [...new Set([...text].filter((ch) => ch.codePointAt(0)! > 0xff))], [text]);
  if (t.isPending) return <div className="panel"><Loading /></div>;
  if (t.error || !th) return <div className="panel"><ErrorBox error={t.error} /></div>;
  const o = th.order;
  const deadline = th.waitingSince && th.viaAgent ? businessDeadline(th.waitingSince) : null;
  const blocked = th.status === 'blocked';
  const canStart = th.buyerMessages === 0 && !blocked;

  return (
    <div className="panel" style={{ display: 'grid', gridTemplateRows: 'auto 1fr auto', gap: 10, minHeight: 520, maxHeight: '78vh' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', borderBottom: '1px solid var(--line)', paddingBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.4 }}>
          <b>{o?.buyer ?? `Carrinho ${th.packId}`}</b>
          <span className="small muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o?.items ?? '—'}</span>
          <span className="small muted">
            {o ? <>Pedido <Link to={`/pedidos?q=${o.externalId}&period=all`} className="code">{o.externalId}</Link> · {brl(o.total)}{o.logisticType ? ` · ${LOG[o.logisticType] ?? o.logisticType}` : ''}{o.shipmentStatus ? ` · ${SHIP[o.shipmentStatus] ?? o.shipmentStatus}` : ''}{o.at ? ` · ${dateTime(o.at)}` : ''}</> : `Carrinho ${th.packId}`}
          </span>
        </div>
        {th.viaAgent && <Tag tone="info"><Bot size={12} /> assistente de IA do ML</Tag>}
        {deadline && <Tag tone={deadline.getTime() - Date.now() < 12 * 3_600_000 ? 'err' : 'warn'}>{until(deadline)}</Tag>}
        <button type="button" className="icon-btn" aria-label="Fechar conversa" onClick={onClose}><X size={16} /></button>
      </div>

      <div style={{ overflow: 'auto', display: 'grid', gap: 8, alignContent: 'start', padding: '0 2px' }}>
        {th.syncError && <div className="notice warn small">Não foi possível atualizar agora: {th.syncError}</div>}
        {th.messages.length === 0 && <div className="empty small">Nenhuma mensagem neste carrinho ainda.</div>}
        {th.messages.map((m) => (
          <div key={m.id} style={{ justifySelf: m.fromSeller ? 'end' : 'start', maxWidth: '78%', display: 'grid', gap: 2 }}>
            <div style={{ background: m.fromSeller ? 'color-mix(in srgb, var(--accent, #2f6bff) 14%, var(--surface))' : 'var(--surface-2)', borderRadius: 10, padding: '8px 11px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {m.text || <span className="muted">(sem texto)</span>}
              {m.attachments.length > 0 && (
                <div style={{ display: 'grid', gap: 2, marginTop: 4 }}>
                  {m.attachments.map((a) => <a key={a.filename} className="small" href={`/api/v1/channels/mercadolivre/messages/${id}/attachments/${encodeURIComponent(a.filename)}`} target="_blank" rel="noreferrer"><Paperclip size={11} /> {a.name}</a>)}
                </div>
              )}
            </div>
            <span className="small muted" style={{ justifySelf: m.fromSeller ? 'end' : 'start' }}>
              {m.fromSeller ? (m.sentBy ? `${m.sentBy} · ` : 'pelo ML · ') : m.fromAgent ? 'assistente do ML · ' : 'comprador · '}{dateTime(m.sentAt)}
              {m.fromSeller && (m.readAt ? <> · <CheckCheck size={12} /> lida</> : <> · <Check size={12} /></>)}
              {m.status && !['available'].includes(m.status) && ` · ${m.status === 'pending_review' ? 'em revisão no ML' : m.status === 'moderated' ? 'moderada' : m.status === 'rejected' ? 'recusada' : m.status}`}
              {m.moderation && ` · moderada: ${MOD[m.moderation.reason ?? ''] ?? m.moderation.reason ?? m.moderation.status}`}
            </span>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div style={{ borderTop: '1px solid var(--line)', paddingTop: 8, display: 'grid', gap: 6 }}>
        {blocked ? (
          <div className="notice warn small"><Lock size={13} /> <b>Conversa bloqueada pelo Mercado Livre.</b> {th.blockReason}</div>
        ) : canStart ? (
          <StartConversation id={id} />
        ) : (
          <>
            <textarea className="input" rows={3} maxLength={th.maxLength} placeholder={th.viaAgent ? 'Mensagem para o comprador (passa pelo assistente do ML)…' : 'Mensagem para o comprador…'}
              value={text} onChange={(e) => setText(e.target.value)} style={{ resize: 'vertical', minHeight: 70, height: 'auto', padding: '8px 10px' }}
              onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && text.trim() && !bad.length) send.mutate(); }} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input ref={fileRef} type="file" multiple hidden accept=".jpg,.jpeg,.png,.gif,.pdf,.heic,.heif,image/*,application/pdf"
                onChange={(e) => { setFiles([...files, ...Array.from(e.target.files ?? [])].slice(0, 5)); e.target.value = ''; }} />
              <button type="button" className="btn small ghost" onClick={() => fileRef.current?.click()}><Paperclip size={14} /> Anexar</button>
              {files.map((f, i) => <Tag key={i} tone="off">{f.name} <button type="button" className="icon-btn" style={{ width: 16, height: 16 }} aria-label="Tirar" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X size={11} /></button></Tag>)}
              <span className="small muted" style={{ color: text.length > th.maxLength ? 'var(--err)' : undefined }}>{text.length}/{th.maxLength}</span>
              {bad.length > 0 && <span className="small" style={{ color: 'var(--err)' }}>O ML não aceita: {bad.join(' ')}</span>}
              <span style={{ flex: 1 }} />
              <button type="button" className="btn primary small" disabled={!text.trim() || bad.length > 0 || send.isPending} onClick={() => send.mutate()}><Send size={14} /> {send.isPending ? 'Enviando…' : 'Enviar'}</button>
            </div>
            {send.error && <ErrorBox error={send.error} />}
          </>
        )}
      </div>
    </div>
  );
}

/** Pedido sem conversa (Mercado Envios): só pelos motivos do Mercado Livre. */
function StartConversation({ id }: { id: string }) {
  const qc = useQueryClient();
  const o = useQuery({ queryKey: ['message-options', id], queryFn: () => api<{ exception: boolean; options: Option[] }>(`/channels/mercadolivre/messages/${id}/options`) });
  const [text, setText] = useState('');
  const go = useMutation({
    mutationFn: (b: { optionId: string; templateId?: string; text?: string }) => api(`/channels/mercadolivre/messages/${id}/options`, { method: 'POST', json: b }),
    onSuccess: () => { setText(''); void qc.invalidateQueries({ queryKey: ['message-thread', id] }); void qc.invalidateQueries({ queryKey: ['message-options', id] }); },
  });
  if (o.isPending) return <Loading />;
  if (o.error) return <ErrorBox error={o.error} />;
  if (o.data!.exception) return <div className="notice small">Este pedido é um caso de exceção do Mercado Livre: a conversa está liberada, mas o comprador ainda não escreveu. Recarregue para escrever normalmente.</div>;
  const opts = o.data!.options;
  const free = opts.find((x) => x.kind === 'text' && x.id === 'OTHER');
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div className="small"><b>Iniciar a conversa</b> — em vendas com Mercado Envios o Mercado Livre só deixa o vendedor começar por um destes motivos:</div>
      {opts.length === 0 && <div className="small muted">O Mercado Livre não liberou nenhum motivo para este pedido agora.</div>}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {opts.filter((x) => x.kind === 'template').map((x) => (
          <button key={x.id} type="button" className="btn small" disabled={go.isPending || x.capAvailable === 0} title={x.templates[0]?.text ?? undefined}
            onClick={() => { if (window.confirm(`Enviar a mensagem pronta do Mercado Livre: "${x.label}"?${x.templates[0]?.text ? `\n\n${x.templates[0].text}` : ''}`)) go.mutate({ optionId: x.id, templateId: x.templates[0]?.id }); }}>
            {x.label}{x.capAvailable != null ? ` (${x.capAvailable})` : ''}
          </button>
        ))}
      </div>
      {free && (
        <>
          <textarea className="input" rows={2} maxLength={free.charLimit ?? 350} placeholder={`Outro assunto (texto livre, até ${free.charLimit ?? 350} caracteres)…`} value={text} onChange={(e) => setText(e.target.value)} style={{ resize: 'vertical', minHeight: 56, height: 'auto', padding: '8px 10px' }} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="small muted">{free.capAvailable != null ? `${free.capAvailable} mensage${free.capAvailable === 1 ? 'm' : 'ns'} disponíve${free.capAvailable === 1 ? 'l' : 'is'} até o comprador responder` : ''}</span>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn primary small" disabled={!text.trim() || go.isPending || free.capAvailable === 0} onClick={() => go.mutate({ optionId: 'OTHER', text })}><Send size={14} /> Enviar</button>
          </div>
        </>
      )}
      {go.error && <ErrorBox error={go.error} />}
    </div>
  );
}
